import React, { useState, useMemo } from 'react';
import {
  ChevronLeft, Calculator, Gauge, Zap, CircleDot,
  RefreshCw, Info, Copy, CheckCircle
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const materials = [
  { value: 'aluminum-6061', label: 'Aluminum 6061-T6', sfm_rough: 600, sfm_finish: 800, feed_rough: 0.010, feed_finish: 0.005, k: 1.0 },
  { value: 'aluminum-7075', label: 'Aluminum 7075-T6', sfm_rough: 500, sfm_finish: 700, feed_rough: 0.010, feed_finish: 0.005, k: 1.0 },
  { value: 'steel-1018', label: 'Steel 1018 (Mild)', sfm_rough: 400, sfm_finish: 500, feed_rough: 0.010, feed_finish: 0.005, k: 0.85 },
  { value: 'steel-4140', label: 'Steel 4140', sfm_rough: 300, sfm_finish: 400, feed_rough: 0.008, feed_finish: 0.004, k: 0.75 },
  { value: 'steel-4340', label: 'Steel 4340', sfm_rough: 250, sfm_finish: 350, feed_rough: 0.008, feed_finish: 0.004, k: 0.7 },
  { value: 'stainless-303', label: 'Stainless 303', sfm_rough: 250, sfm_finish: 350, feed_rough: 0.008, feed_finish: 0.004, k: 0.65 },
  { value: 'stainless-304', label: 'Stainless 304', sfm_rough: 200, sfm_finish: 280, feed_rough: 0.006, feed_finish: 0.003, k: 0.6 },
  { value: 'stainless-316', label: 'Stainless 316', sfm_rough: 180, sfm_finish: 250, feed_rough: 0.006, feed_finish: 0.003, k: 0.55 },
  { value: 'titanium-6al4v', label: 'Titanium 6Al-4V', sfm_rough: 100, sfm_finish: 150, feed_rough: 0.005, feed_finish: 0.003, k: 0.4 },
  { value: 'brass-360', label: 'Brass C360', sfm_rough: 500, sfm_finish: 700, feed_rough: 0.010, feed_finish: 0.005, k: 1.1 },
  { value: 'cast-iron', label: 'Cast Iron (Gray)', sfm_rough: 300, sfm_finish: 400, feed_rough: 0.010, feed_finish: 0.005, k: 0.8 },
  { value: 'plastic-delrin', label: 'Delrin/Acetal', sfm_rough: 800, sfm_finish: 1000, feed_rough: 0.012, feed_finish: 0.006, k: 1.3 },
  { value: 'copper', label: 'Copper', sfm_rough: 300, sfm_finish: 400, feed_rough: 0.008, feed_finish: 0.004, k: 0.9 },
  { value: 'inconel-718', label: 'Inconel 718', sfm_rough: 80, sfm_finish: 120, feed_rough: 0.004, feed_finish: 0.002, k: 0.3 },
];

const operations = [
  { value: 'od_rough', label: 'OD Roughing', docRange: [0.050, 0.150], feedMult: 1.0 },
  { value: 'od_finish', label: 'OD Finishing', docRange: [0.005, 0.020], feedMult: 0.6 },
  { value: 'facing', label: 'Facing', docRange: [0.050, 0.100], feedMult: 0.9 },
  { value: 'boring', label: 'ID Boring', docRange: [0.030, 0.100], feedMult: 0.8 },
  { value: 'grooving', label: 'Grooving', docRange: [null, null], feedMult: 0.4 },
  { value: 'parting', label: 'Parting/Cutoff', docRange: [null, null], feedMult: 0.3 },
  { value: 'threading', label: 'Threading', docRange: [null, null], feedMult: null },
  { value: 'drilling', label: 'Drilling', docRange: [null, null], feedMult: 0.7 },
];

const insertGrades = [
  { value: 'P', label: 'P (Steel)', color: 'bg-blue-500', materials: ['steel', 'cast'] },
  { value: 'M', label: 'M (Stainless)', color: 'bg-yellow-500', materials: ['stainless'] },
  { value: 'K', label: 'K (Cast Iron)', color: 'bg-red-500', materials: ['cast-iron'] },
  { value: 'N', label: 'N (Non-Ferrous)', color: 'bg-green-500', materials: ['aluminum', 'brass', 'copper', 'plastic'] },
  { value: 'S', label: 'S (Super Alloy)', color: 'bg-amber-700', materials: ['titanium', 'inconel'] },
];

const FeedsSpeedsPage = () => {
  const navigate = useNavigate();
  const [material, setMaterial] = useState('steel-4140');
  const [operation, setOperation] = useState('od_rough');
  const [diameter, setDiameter] = useState(1.5);
  const [noseRadius, setNoseRadius] = useState(0.031);
  const [units, setUnits] = useState('inch');
  const [copied, setCopied] = useState('');

  const mat = materials.find(m => m.value === material) || materials[3];
  const op = operations.find(o => o.value === operation) || operations[0];

  const results = useMemo(() => {
    const isRough = operation.includes('rough') || operation === 'facing';
    const sfm = isRough ? mat.sfm_rough : mat.sfm_finish;
    const feed = isRough ? mat.feed_rough : mat.feed_finish;
    const adjFeed = op.feedMult ? feed * op.feedMult / (isRough ? 1.0 : 0.6) : feed;
    
    // RPM = (SFM × 12) / (π × D) for inch
    const rpm = units === 'inch' 
      ? Math.round((sfm * 12) / (Math.PI * diameter))
      : Math.round((sfm * 1000) / (Math.PI * diameter));
    
    // Max RPM limits
    let maxRpm = 3500;
    if (diameter > 2) maxRpm = 2500;
    if (diameter > 4) maxRpm = 1500;
    if (diameter > 6) maxRpm = 1000;
    const clampedRpm = Math.min(rpm, maxRpm);
    
    // DOC
    const doc = op.docRange[0] ? ((op.docRange[0] + op.docRange[1]) / 2) : null;
    
    // Surface finish Ra = f² / (32 × r) × 1000 (in µin for inch)
    const ra = noseRadius > 0 ? ((adjFeed * adjFeed) / (32 * noseRadius)) * 1000000 : null;
    const raUm = ra ? ra * 0.0254 : null; // Convert µin to µm
    
    // MRR (Material Removal Rate) = 12 × SFM × feed × DOC for inch
    const mrr = doc ? (12 * sfm * adjFeed * doc) : null;
    
    // Recommended grade
    const grade = insertGrades.find(g => g.materials.some(m => material.includes(m))) || insertGrades[0];
    
    // Thread info
    const isThread = operation === 'threading';
    const threadRpm = isThread ? Math.min(Math.round(sfm * 0.4 * 12 / (Math.PI * diameter)), 1200) : null;
    
    return { sfm, feed: adjFeed, rpm, clampedRpm, maxRpm, doc, ra, raUm, mrr, grade, isThread, threadRpm };
  }, [material, operation, diameter, noseRadius, units, mat, op]);

  const copyValue = (label, value) => {
    navigator.clipboard.writeText(String(value));
    setCopied(label);
    setTimeout(() => setCopied(''), 1500);
  };

  const gcodeSnippet = useMemo(() => {
    if (results.isThread) {
      return `G97 S${results.threadRpm} M03   (FIXED RPM FOR THREADING)\nG00 X${diameter.toFixed(3)} Z0.200`;
    }
    return `G96 S${results.sfm} M03      (CSS ${results.sfm} SFM)\nG50 S${results.maxRpm}            (MAX RPM LIMIT)\nG00 X${(diameter + 0.1).toFixed(3)} Z0.100 M08\nG01 Z-1.000 F${results.feed.toFixed(4)}  (FEED ${results.feed.toFixed(4)} IPR)${results.doc ? `\n(DOC: ${results.doc.toFixed(3)}" PER SIDE)` : ''}`;
  }, [results, diameter]);

  return (
    <div className="min-h-screen bg-industrial-bg pb-20">
      <div className="sticky top-14 z-20 bg-industrial-surface border-b border-industrial-border px-4 py-3">
        <div className="flex items-center justify-between">
          <button onClick={() => navigate('/')} className="flex items-center gap-1 text-industrial-text-secondary hover:text-industrial-text" data-testid="back-btn">
            <ChevronLeft className="w-5 h-5" /><span className="text-sm">Back</span>
          </button>
          <h2 className="font-heading font-bold text-cyan-400 flex items-center gap-2">
            <Calculator className="w-5 h-5" />FEEDS & SPEEDS
          </h2>
          <div className="w-16" />
        </div>
      </div>

      <div className="p-4 space-y-4">
        {/* Units Toggle */}
        <div className="flex gap-2">
          {['inch', 'metric'].map(u => (
            <button key={u} onClick={() => setUnits(u)}
              className={`flex-1 py-2 rounded-lg text-sm font-medium border transition-all ${units === u ? 'bg-cyan-500/20 border-cyan-400 text-cyan-400' : 'bg-industrial-surface border-industrial-border text-industrial-text-secondary'}`}
              data-testid={`units-${u}`}
            >
              {u === 'inch' ? 'Inch (IPR/SFM)' : 'Metric (mm/rev, m/min)'}
            </button>
          ))}
        </div>

        {/* Material */}
        <div className="bg-industrial-surface border border-industrial-border rounded-lg p-3">
          <label className="text-xs text-industrial-text-secondary mb-2 block">Material</label>
          <select value={material} onChange={e => setMaterial(e.target.value)}
            className="w-full bg-industrial-bg border border-industrial-border rounded-lg p-3 text-industrial-text"
            data-testid="material-select"
          >
            {materials.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
          </select>
          <div className="flex items-center gap-2 mt-2">
            <span className="text-xs text-industrial-text-secondary">Recommended Grade:</span>
            <span className={`text-xs px-2 py-0.5 rounded ${results.grade.color} text-white font-medium`}>
              ISO {results.grade.value} — {results.grade.label}
            </span>
          </div>
        </div>

        {/* Operation */}
        <div className="bg-industrial-surface border border-industrial-border rounded-lg p-3">
          <label className="text-xs text-industrial-text-secondary mb-2 block">Operation</label>
          <div className="grid grid-cols-4 gap-1.5">
            {operations.map(o => (
              <button key={o.value} onClick={() => setOperation(o.value)}
                className={`p-2 rounded-lg border text-[11px] font-medium transition-all ${operation === o.value ? 'bg-cyan-500/20 border-cyan-400 text-cyan-400' : 'bg-industrial-bg border-industrial-border text-industrial-text-secondary hover:border-cyan-400/50'}`}
                data-testid={`op-${o.value}`}
              >{o.label}</button>
            ))}
          </div>
        </div>

        {/* Diameter & Nose Radius */}
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-industrial-surface border border-industrial-border rounded-lg p-3">
            <label className="text-xs text-industrial-text-secondary mb-1 block">Cutting Diameter ({units === 'inch' ? 'in' : 'mm'})</label>
            <input type="number" step="0.001" value={diameter} onChange={e => setDiameter(parseFloat(e.target.value) || 0.1)}
              className="w-full bg-industrial-bg border border-industrial-border rounded-lg p-2.5 text-industrial-text text-lg font-mono"
              data-testid="diameter-input"
            />
          </div>
          <div className="bg-industrial-surface border border-industrial-border rounded-lg p-3">
            <label className="text-xs text-industrial-text-secondary mb-1 block">Nose Radius ({units === 'inch' ? 'in' : 'mm'})</label>
            <input type="number" step="0.001" value={noseRadius} onChange={e => setNoseRadius(parseFloat(e.target.value) || 0.001)}
              className="w-full bg-industrial-bg border border-industrial-border rounded-lg p-2.5 text-industrial-text text-lg font-mono"
              data-testid="nose-radius-input"
            />
          </div>
        </div>

        {/* Results */}
        <div className="bg-industrial-surface border border-cyan-500/30 rounded-xl p-4 space-y-3" data-testid="results-panel">
          <h3 className="text-sm font-heading font-bold text-cyan-400 flex items-center gap-2">
            <Zap className="w-4 h-4" />CALCULATED PARAMETERS
          </h3>
          
          <div className="grid grid-cols-2 gap-3">
            {/* Surface Speed */}
            <ResultCard
              label={units === 'inch' ? 'Surface Speed (SFM)' : 'Surface Speed (m/min)'}
              value={results.sfm}
              code={`G96 S${results.sfm}`}
              onCopy={() => copyValue('sfm', results.sfm)}
              copied={copied === 'sfm'}
            />
            
            {/* RPM */}
            <ResultCard
              label="Spindle RPM"
              value={results.isThread ? results.threadRpm : results.clampedRpm}
              code={results.isThread ? `G97 S${results.threadRpm}` : `G50 S${results.maxRpm}`}
              warn={results.rpm > results.maxRpm && !results.isThread ? `Calc: ${results.rpm} → Clamped` : null}
              onCopy={() => copyValue('rpm', results.isThread ? results.threadRpm : results.clampedRpm)}
              copied={copied === 'rpm'}
            />
            
            {/* Feed Rate */}
            <ResultCard
              label={units === 'inch' ? 'Feed (IPR)' : 'Feed (mm/rev)'}
              value={results.feed.toFixed(4)}
              code={`F${results.feed.toFixed(4)}`}
              onCopy={() => copyValue('feed', results.feed.toFixed(4))}
              copied={copied === 'feed'}
            />
            
            {/* DOC */}
            <ResultCard
              label="Depth of Cut"
              value={results.doc ? `${results.doc.toFixed(3)}"` : 'N/A'}
              code={results.doc ? `(DOC ${results.doc.toFixed(3)})` : ''}
              onCopy={() => results.doc && copyValue('doc', results.doc.toFixed(3))}
              copied={copied === 'doc'}
            />
          </div>

          {/* Surface Finish */}
          {results.ra && !results.isThread && (
            <div className="bg-industrial-bg rounded-lg p-3 border border-industrial-border">
              <div className="flex items-center justify-between">
                <span className="text-xs text-industrial-text-secondary">Est. Surface Finish (Ra)</span>
                <span className="font-mono text-sm text-industrial-text">
                  {results.ra.toFixed(0)} µin / {results.raUm.toFixed(1)} µm
                </span>
              </div>
              <div className="mt-1.5 h-2 bg-industrial-surface rounded-full overflow-hidden">
                <div 
                  className={`h-full rounded-full transition-all ${results.ra < 32 ? 'bg-green-500' : results.ra < 63 ? 'bg-cyan-500' : results.ra < 125 ? 'bg-yellow-500' : 'bg-red-500'}`}
                  style={{ width: `${Math.min(100, (results.ra / 250) * 100)}%` }}
                />
              </div>
              <div className="flex justify-between mt-1 text-[10px] text-industrial-text-secondary">
                <span>Mirror</span><span>Fine</span><span>Medium</span><span>Rough</span>
              </div>
            </div>
          )}

          {/* G-code Snippet */}
          <div className="bg-industrial-bg rounded-lg p-3 border border-industrial-border">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-cyan-400 font-medium">G-Code Snippet</span>
              <button onClick={() => { navigator.clipboard.writeText(gcodeSnippet); setCopied('gcode'); setTimeout(() => setCopied(''), 1500); }}
                className="text-xs text-industrial-text-secondary hover:text-cyan-400 flex items-center gap-1"
                data-testid="copy-snippet-btn"
              >
                {copied === 'gcode' ? <CheckCircle className="w-3 h-3 text-green-400" /> : <Copy className="w-3 h-3" />}
                {copied === 'gcode' ? 'Copied' : 'Copy'}
              </button>
            </div>
            <pre className="font-mono text-xs text-industrial-text whitespace-pre-wrap leading-relaxed">{gcodeSnippet}</pre>
          </div>
        </div>

        {/* Formulas Reference */}
        <details className="bg-industrial-surface border border-industrial-border rounded-lg">
          <summary className="p-3 text-sm text-industrial-text-secondary cursor-pointer hover:text-industrial-text flex items-center gap-2">
            <Info className="w-4 h-4" /> Formulas Reference
          </summary>
          <div className="p-3 pt-0 text-xs text-industrial-text-secondary space-y-1 font-mono">
            <p>RPM = (SFM × 12) / (π × D)</p>
            <p>Ra = (f² / (32 × r)) × 10⁶ µin</p>
            <p>MRR = 12 × SFM × f × DOC in³/min</p>
            <p className="text-industrial-text-secondary/50 mt-2">Source: machining.cloud methodology + FANUC OT reference data</p>
          </div>
        </details>
      </div>
    </div>
  );
};

const ResultCard = ({ label, value, code, warn, onCopy, copied }) => (
  <div className="bg-industrial-bg rounded-lg p-3 border border-industrial-border cursor-pointer hover:border-cyan-400/50 transition-colors" onClick={onCopy}>
    <div className="text-[10px] text-industrial-text-secondary">{label}</div>
    <div className="text-xl font-mono font-bold text-industrial-text mt-0.5">{value}</div>
    {code && <div className="text-[10px] font-mono text-cyan-400/70 mt-1">{code}</div>}
    {warn && <div className="text-[10px] text-yellow-400 mt-0.5">{warn}</div>}
    {copied && <div className="text-[10px] text-green-400 mt-0.5">Copied!</div>}
  </div>
);

export default FeedsSpeedsPage;
